// Admin → Payments. The money console: revenue tiles, a filterable invoice
// table, an invoice drawer with the refund ledger, and the refund dialog.
//
// First surface built on the component system (M7c / P3). Everything that used
// to be hand-rolled here — the table, the tiles, the loading and empty states,
// the currency formatting — now comes from ~/components.
//
// 🔴 Refunding requires an explicit REASON and supports PARTIAL amounts. The
// previous version issued a full refund with no reason and marked the invoice
// `void`, erasing that it was ever paid.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useMemo, useState } from 'react';

import {
  ConfirmDialog,
  DataTable,
  DetailRow,
  DetailSection,
  Drawer,
  FilterBar,
  Icon,
  Money,
  MoneyTotals,
  PageHeader,
  Pagination,
  StatGrid,
  StatTile,
  StatusBadge,
  actionsColumn,
  paginate,
  sortRows,
  useTableState,
  useToast,
  type Column,
} from '~/components';
import { formatMinorAmount, parseMinorAmount } from '~/lib/money';
import {
  getInvoice,
  getPaymentsSummary,
  listInvoices,
  refundInvoice,
  type AdminInvoiceRow,
} from '~/lib/payments';

export const Route = createFileRoute('/_authed/payments')({
  component: PaymentsPage,
});

const STATUS_OPTIONS = [
  { value: 'open', label: 'Open' },
  { value: 'paid', label: 'Paid' },
  { value: 'partially_refunded', label: 'Partially refunded' },
  { value: 'refunded', label: 'Refunded' },
  { value: 'void', label: 'Void' },
  { value: 'uncollectible', label: 'Uncollectible' },
];

function PaymentsPage() {
  const table = useTableState({
    defaultSort: { key: 'issuedAt', direction: 'desc' },
    filterKeys: ['status'],
  });
  const [openInvoiceId, setOpenInvoiceId] = useState<string | null>(null);

  const summary = useQuery({
    queryKey: ['payments', 'summary'],
    queryFn: ({ signal }) => getPaymentsSummary(signal),
  });

  const invoices = useQuery({
    queryKey: ['payments', 'invoices', table.filters.status ?? 'all'],
    queryFn: ({ signal }) => listInvoices(table.filters.status, signal),
  });

  // Search and sort happen client-side: the endpoint returns at most 200 rows,
  // and filtering in the browser keeps the interaction instant. When this list
  // outgrows that, the query moves server-side and this block goes away.
  const filtered = useMemo(() => {
    const rows = invoices.data ?? [];
    const q = table.q.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        r.ownerLabel.toLowerCase().includes(q) ||
        r.id.toLowerCase().includes(q) ||
        r.currency.toLowerCase().includes(q),
    );
  }, [invoices.data, table.q]);

  const sorted = useMemo(
    () =>
      sortRows(filtered, table.sort, {
        ownerLabel: (r) => r.ownerLabel,
        amountMinor: (r) => r.amountMinor,
        status: (r) => r.status,
        issuedAt: (r) => r.issuedAt,
        paidAt: (r) => r.paidAt,
        refundedMinor: (r) => r.refundedMinor,
      }),
    [filtered, table.sort],
  );

  const pageRows = useMemo(
    () => paginate(sorted, table.page, table.pageSize),
    [sorted, table.page, table.pageSize],
  );

  const columns: Column<AdminInvoiceRow>[] = [
    {
      key: 'ownerLabel',
      header: 'Customer',
      sortable: true,
      alwaysVisible: true,
      render: (r) => (
        <div className="min-w-0">
          <p className="truncate font-medium text-admin-900">{r.ownerLabel}</p>
          <p className="truncate text-xs text-admin-400">{r.ownerType}</p>
        </div>
      ),
    },
    {
      key: 'amountMinor',
      header: 'Amount',
      sortable: true,
      align: 'end',
      render: (r) => <Money amountMinor={r.amountMinor} currency={r.currency} emphasis="strong" />,
    },
    {
      key: 'refundedMinor',
      header: 'Refunded',
      sortable: true,
      align: 'end',
      render: (r) =>
        r.refundedMinor > 0 ? (
          <Money amountMinor={-r.refundedMinor} currency={r.currency} signed showCurrency={false} />
        ) : (
          <span className="text-admin-300">—</span>
        ),
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (r) => <StatusBadge status={r.status} dot />,
    },
    {
      key: 'issuedAt',
      header: 'Issued',
      sortable: true,
      hideBelow: 'md',
      render: (r) => <span className="text-admin-500">{formatDate(r.issuedAt)}</span>,
    },
    {
      key: 'paidAt',
      header: 'Paid',
      sortable: true,
      hideBelow: 'lg',
      render: (r) =>
        r.paidAt ? (
          <span className="text-admin-500">{formatDate(r.paidAt)}</span>
        ) : (
          <span className="text-admin-300">—</span>
        ),
    },
    {
      key: 'id',
      header: 'Invoice',
      hidden: true,
      render: (r) => <code className="text-xs text-admin-400">{r.id.slice(0, 8)}</code>,
    },
    actionsColumn<AdminInvoiceRow>({
      label: (r) => `invoice for ${r.ownerLabel}`,
      primary: (r) => ({
        id: 'open',
        label: 'Open invoice',
        icon: 'eye',
        onSelect: () => setOpenInvoiceId(r.id),
      }),
      actions: (r) => [
        {
          id: 'copy-id',
          label: 'Copy invoice id',
          icon: 'copy',
          onSelect: () => void navigator.clipboard?.writeText(r.id),
        },
        {
          id: 'refund',
          label: 'Issue refund',
          icon: 'undo',
          // Refunding is destructive in the money sense, so it is red — but the
          // amount + reason still come from the drawer's dialog, never from a
          // one-click menu item.
          destructive: true,
          onSelect: () => setOpenInvoiceId(r.id),
          disabled: r.status !== 'paid' && r.status !== 'partially_refunded',
          disabledReason:
            r.status === 'refunded'
              ? 'This invoice is already fully refunded.'
              : 'Only paid invoices can be refunded.',
        },
      ],
    }),
  ];

  const collected = toTotals(summary.data?.collectedByCurrency);
  const refunded = toTotals(summary.data?.refundedByCurrency);
  const net = toTotals(summary.data?.netByCurrency);
  const subs = summary.data?.subscriptionsByStatus ?? {};

  return (
    <div className="mx-auto max-w-7xl">
      <PageHeader
        title="Payments"
        description="Invoices across every family and school, with the refund ledger."
      />

      <StatGrid className="mb-6">
        <StatTile
          label="Collected"
          icon="money"
          loading={summary.isLoading}
          value={<MoneyTotals totals={collected} />}
          hint="gross, including later refunds"
        />
        <StatTile
          label="Refunded"
          icon="undo"
          loading={summary.isLoading}
          value={<MoneyTotals totals={refunded} />}
          hint="succeeded refunds only"
        />
        <StatTile
          label="Net"
          icon="chart"
          loading={summary.isLoading}
          value={<MoneyTotals totals={net} />}
          hint="collected − refunded"
        />
        <StatTile
          label="Subscriptions"
          icon="card"
          loading={summary.isLoading}
          value={Object.values(subs)
            .reduce((a, b) => a + b, 0)
            .toLocaleString('en-US')}
          hint={
            Object.keys(subs).length > 0 ? (
              <span className="flex flex-wrap gap-1">
                {Object.entries(subs).map(([status, count]) => (
                  <StatusBadge key={status} status={status} label={`${status} ${count}`} />
                ))}
              </span>
            ) : (
              'none yet'
            )
          }
        />
      </StatGrid>

      <DataTable
        columns={columns}
        rows={pageRows}
        rowKey={(r) => r.id}
        loading={invoices.isLoading}
        error={invoices.error}
        onRetry={() => void invoices.refetch()}
        sort={table.sort}
        onSortChange={table.setSort}
        onRowClick={(r) => setOpenInvoiceId(r.id)}
        isRowActive={(r) => r.id === openInvoiceId}
        columnVisibility
        caption="Invoices"
        empty={{
          icon: 'receipt',
          title: table.isFiltered ? 'No invoices match these filters' : 'No invoices yet',
          description: table.isFiltered
            ? 'Try clearing the filters to see everything.'
            : 'Invoices appear here as soon as customers start paying.',
        }}
        toolbar={
          <FilterBar
            query={table.q}
            onQueryChange={table.setQuery}
            searchPlaceholder="Search customer or invoice id…"
            filters={[
              {
                key: 'status',
                label: 'Status',
                value: table.filters.status ?? '',
                options: STATUS_OPTIONS,
              },
            ]}
            onFilterChange={table.setFilter}
            onClear={table.clearFilters}
            isFiltered={table.isFiltered}
          />
        }
        footer={
          <Pagination
            page={table.page}
            pageSize={table.pageSize}
            total={sorted.length}
            onPageChange={table.setPage}
            onPageSizeChange={table.setPageSize}
          />
        }
      />

      <InvoiceDrawer invoiceId={openInvoiceId} onClose={() => setOpenInvoiceId(null)} />
    </div>
  );
}

function InvoiceDrawer({ invoiceId, onClose }: { invoiceId: string | null; onClose: () => void }) {
  const [refundOpen, setRefundOpen] = useState(false);

  const detail = useQuery({
    queryKey: ['payments', 'invoice', invoiceId],
    queryFn: ({ signal }) => getInvoice(invoiceId!, signal),
    enabled: invoiceId != null,
  });

  const invoice = detail.data?.invoice;

  return (
    <>
      <Drawer
        open={invoiceId != null}
        onOpenChange={(open) => !open && onClose()}
        title="Invoice"
        description={detail.data?.owner.label}
        badge={invoice && <StatusBadge status={invoice.status} dot />}
        width="lg"
        footer={
          invoice && invoice.refundableMinor > 0 ? (
            <button
              type="button"
              onClick={() => setRefundOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-lg border border-error-500 px-3.5 py-2 text-sm font-semibold text-error-700 transition-colors hover:bg-error-50"
            >
              <Icon name="undo" className="h-4 w-4" />
              Issue refund
            </button>
          ) : null
        }
      >
        {detail.isLoading && <div className="h-40 animate-pulse rounded-xl bg-admin-100" />}

        {invoice && (
          <>
            <DetailSection title="Amount">
              <DetailRow label="Total">
                <Money
                  amountMinor={invoice.amountMinor}
                  currency={invoice.currency}
                  emphasis="strong"
                />
              </DetailRow>
              {invoice.subtotalMinor != null && invoice.discountMinor > 0 && (
                <>
                  <DetailRow label="Subtotal">
                    <Money amountMinor={invoice.subtotalMinor} currency={invoice.currency} />
                  </DetailRow>
                  <DetailRow label="Discount">
                    <Money
                      amountMinor={-invoice.discountMinor}
                      currency={invoice.currency}
                      signed
                    />
                    {invoice.couponCode && (
                      <span className="ms-2 text-xs text-admin-400">{invoice.couponCode}</span>
                    )}
                  </DetailRow>
                </>
              )}
              {invoice.refundedMinor > 0 && (
                <DetailRow label="Refunded">
                  <Money amountMinor={-invoice.refundedMinor} currency={invoice.currency} signed />
                </DetailRow>
              )}
              <DetailRow label="Refundable">
                <Money amountMinor={invoice.refundableMinor} currency={invoice.currency} />
              </DetailRow>
            </DetailSection>

            <DetailSection title="Details">
              <DetailRow label="Plan">{invoice.planSlug ?? '—'}</DetailRow>
              <DetailRow label="Issued">{formatDate(invoice.issuedAt)}</DetailRow>
              <DetailRow label="Paid">
                {invoice.paidAt ? formatDate(invoice.paidAt) : '—'}
              </DetailRow>
              <DetailRow label="Provider ref">
                {invoice.providerRef ? (
                  <code className="break-all text-xs">{invoice.providerRef}</code>
                ) : (
                  <span className="text-admin-400">none — cannot be refunded</span>
                )}
              </DetailRow>
              <DetailRow label="Invoice id">
                <code className="break-all text-xs">{invoice.id}</code>
              </DetailRow>
            </DetailSection>

            <DetailSection title={`Refunds (${invoice.refunds.length})`}>
              {invoice.refunds.length === 0 ? (
                <p className="py-2 text-sm text-admin-400">No refunds on this invoice.</p>
              ) : (
                invoice.refunds.map((r) => (
                  <DetailRow key={r.id} label={<StatusBadge status={r.status} />}>
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                      <span className="text-sm text-admin-700">{r.reason}</span>
                      <Money amountMinor={-r.amountMinor} currency={r.currency} signed />
                    </div>
                    <p className="mt-0.5 text-xs text-admin-400">
                      {formatDate(r.refundedAt ?? r.createdAt)}
                    </p>
                  </DetailRow>
                ))
              )}
            </DetailSection>
          </>
        )}
      </Drawer>

      {invoice && (
        <RefundDialog
          open={refundOpen}
          onOpenChange={setRefundOpen}
          invoiceId={invoice.id}
          currency={invoice.currency}
          refundableMinor={invoice.refundableMinor}
        />
      )}
    </>
  );
}

function RefundDialog({
  open,
  onOpenChange,
  invoiceId,
  currency,
  refundableMinor,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  invoiceId: string;
  currency: string;
  refundableMinor: number;
}) {
  const qc = useQueryClient();
  const { toast } = useToast();
  // Default to the full remaining balance — the common case — but let the
  // operator reduce it. Parsed against THIS invoice's currency, so a KWD
  // refund keeps three decimals.
  const [amountText, setAmountText] = useState(() => formatMinorAmount(refundableMinor, currency));
  const [reason, setReason] = useState('');

  const parsedMinor = parseMinorAmount(amountText, currency);
  const amountError =
    parsedMinor == null
      ? 'Enter a valid amount'
      : parsedMinor <= 0
        ? 'Amount must be more than zero'
        : parsedMinor > refundableMinor
          ? `Only ${formatMinorAmount(refundableMinor, currency)} ${currency} can still be refunded`
          : null;

  const mutation = useMutation({
    mutationFn: () =>
      refundInvoice(invoiceId, { amountMinor: parsedMinor!, reason: reason.trim() }),
    onSuccess: (result) => {
      void qc.invalidateQueries({ queryKey: ['payments'] });
      toast(`Refunded ${formatMinorAmount(result.refund.amountMinor, currency)} ${currency}`, {
        tone: 'success',
      });
    },
  });

  return (
    <ConfirmDialog
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next);
        if (next) {
          setAmountText(formatMinorAmount(refundableMinor, currency));
          setReason('');
        }
      }}
      title="Issue a refund"
      description="This moves money back to the customer and cannot be undone from here. The refund is recorded permanently against this invoice."
      confirmLabel="Refund"
      tone="danger"
      disabled={amountError != null || reason.trim() === ''}
      // Awaited by the dialog: it stays open and shows the error if this
      // rejects, rather than closing over a failed refund.
      onConfirm={async () => {
        await mutation.mutateAsync();
      }}
    >
      <div className="space-y-3">
        <div>
          <label htmlFor="refund-amount" className="block text-sm font-medium text-admin-700">
            Amount ({currency})
          </label>
          <input
            id="refund-amount"
            value={amountText}
            onChange={(e) => setAmountText(e.target.value)}
            inputMode="decimal"
            autoComplete="off"
            className="mt-1.5 h-9 w-full rounded-lg border border-admin-200 px-3 text-sm tabular-nums text-admin-900 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
          />
          <p
            className={amountError ? 'mt-1 text-xs text-error-600' : 'mt-1 text-xs text-admin-400'}
          >
            {amountError ?? `Up to ${formatMinorAmount(refundableMinor, currency)} ${currency}`}
          </p>
        </div>

        <div>
          <label htmlFor="refund-reason" className="block text-sm font-medium text-admin-700">
            Reason
          </label>
          <textarea
            id="refund-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            placeholder="Why is this being refunded?"
            className="mt-1.5 w-full rounded-lg border border-admin-200 px-3 py-2 text-sm text-admin-900 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
          />
          <p className="mt-1 text-xs text-admin-400">
            Required. Stored permanently and shown in the audit log.
          </p>
        </div>
      </div>
    </ConfirmDialog>
  );
}

function toTotals(byCurrency: Record<string, number> | undefined) {
  if (!byCurrency) return [];
  return Object.entries(byCurrency).map(([currency, amountMinor]) => ({ currency, amountMinor }));
}

function formatDate(iso: string): string {
  // Fixed en-GB style so dates never reorder with the viewer's locale — staff
  // compare these across screens and an ambiguous 03/04 is a real hazard.
  return new Date(iso).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}
